import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, SearchX } from 'lucide-react';
import { apiCall } from '@/lib/api-client';
import { navError } from '@/features/navigator/navigator.api';
import { toast } from '@/stores/toast.store';
import { useLang, useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { Pager } from '@/components/chassis/Pager';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { BTN_PRI, BTN_SEC, BTN_SM } from '@/components/brand/brand';
import { formatNy } from '../../navigator/labels';

/**
 * CL-W2 · la lista de auditoría «No aparece en FindHelp» (admin y supervisor).
 *
 * Edd (§11 del plan, selector): «No aparece en FindHelp» → **solo a la lista de
 * auditoría**; el caso SIGUE en la cola del closer, y el «siguiente» no lo
 * vuelve a servir hasta que alguien lo resuelve acá. Resolver limpia la marca
 * y el caso vuelve a los casos por cerrar de su dueño.
 *
 * El contrato (CONTRATO-CLOSER §3, servido por CL-A1):
 *  - `GET /admin/navigation/closer/not-found?page=&pageSize=25` →
 *    `{ rows, total, page, pageSize }`, el más antiguo primero. Es PHI
 *    (nombre + Medicaid ID): el servidor la lee con `readPhi` y audita
 *    `NAV_QUEUE_VIEWED`. Por eso esta lista **no se re-mira sola** (sin
 *    polling) y la pestaña no lleva contador en la barra: un contador la
 *    pediría en cada visita al centro de control.
 *  - `POST /admin/navigation/closer/not-found/:caseId/resolve` con
 *    `{ resolution }` y **SIN nota**: no hay columna donde guardarla y en el
 *    `metadata` del audit no puede ir (decisión del coordinador, 24-oct). El
 *    servidor rechaza un campo de más (400 por `forbidNonWhitelisted`).
 *  - Los errores llegan envueltos por el filtro global: el código está en
 *    `payload.message.code` (lo lee `navError`, que prefiere el anidado).
 *
 * **Sin teléfonos.** La fila trae nombre, Medicaid ID, fechas y nombres de
 * personal; nada más. Si el servidor un día mandara un teléfono, esta pantalla
 * no lo dibuja: solo pinta los campos que nombra.
 */

export const CLOSER_NOT_FOUND_PAGE_SIZE = 25;

export const NOT_FOUND_RESOLUTIONS = ['aparece_ahora', 'resubido', 'otro'] as const;
export type NotFoundResolution = (typeof NOT_FOUND_RESOLUTIONS)[number];

const RESOLUTION_KEYS: Record<NotFoundResolution, StringKey> = {
  aparece_ahora: 'adminNav.closerNotFound.res.aparece_ahora',
  resubido: 'adminNav.closerNotFound.res.resubido',
  otro: 'adminNav.closerNotFound.res.otro',
};

export interface CloserNotFoundRow {
  caseId: string;
  screeningId: string | null;
  patient: { fullName: string; medicaidId: string | null };
  screeningSubmittedAt: string | null;
  uploadedAt: string | null;
  uploaderName: string | null;
  screenerName: string | null;
  flaggedAt: string | null;
  flaggedByName: string | null;
  ownerName: string | null;
}

export interface CloserNotFoundPage {
  rows: CloserNotFoundRow[];
  total: number;
  /** 1-based, como lo manda el servidor. */
  page: number;
  pageSize: number;
}

export interface ResolveNotFoundResult {
  result: 'resolved';
  caseId: string;
  resolution: NotFoundResolution;
}

export const closerNotFoundApi = {
  /** `page` 1-based. El tamaño es SIEMPRE 25 (el contrato). */
  list: (page: number) =>
    apiCall<CloserNotFoundPage>(
      `/admin/navigation/closer/not-found?page=${page}&pageSize=${CLOSER_NOT_FOUND_PAGE_SIZE}`,
    ),
  resolve: (caseId: string, resolution: NotFoundResolution) =>
    apiCall<ResolveNotFoundResult>(`/admin/navigation/closer/not-found/${caseId}/resolve`, {
      method: 'POST',
      body: { resolution },
    }),
};

/**
 * Bajo `['admin','navigation']` a propósito: el botón «Actualizar» del centro
 * de control invalida ese prefijo, así que también refresca esta lista.
 */
export const CLOSER_NOT_FOUND_KEYS = {
  all: ['admin', 'navigation', 'closerNotFound'] as const,
  page: (page: number) => ['admin', 'navigation', 'closerNotFound', page] as const,
};

/**
 * Los códigos que dicen «este caso ya no espera revisión»: se cerró, alguien
 * más lo resolvió, o desapareció. La fila se va y la lista se vuelve a pedir;
 * no es una falla de quien apretó.
 */
const GONE_CODES = new Set(['CASE_ALREADY_CLOSED', 'CLOSER_NOT_FOUND_NOT_FLAGGED', 'CASE_NOT_FOUND']);

export function CloserNotFoundTab(): JSX.Element {
  const t = useT();
  /** 0-based para el Pager; al servidor va +1. */
  const [page, setPage] = useState(0);
  const q = useQuery({
    queryKey: CLOSER_NOT_FOUND_KEYS.page(page),
    queryFn: () => closerNotFoundApi.list(page + 1),
    placeholderData: (prev) => prev,
  });

  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / CLOSER_NOT_FOUND_PAGE_SIZE));
  // Resolver el último de la última página la deja vacía: volver a la que existe.
  useEffect(() => {
    if (q.data && q.data.rows.length === 0 && total > 0 && page > pages - 1) setPage(pages - 1);
  }, [q.data, total, page, pages]);

  return (
    <section aria-labelledby="closer-nf-title" className="flex flex-col gap-3" data-testid="closer-not-found-tab">
      <div>
        <h2 id="closer-nf-title" className="m-0 font-head text-[17px] font-extrabold text-nav-navy">
          {t('adminNav.closerNotFound.title', { n: q.data ? total : '…' })}
        </h2>
        <p className="m-0 mt-1 text-[13.5px] text-nav-slate max-w-[720px]">{t('adminNav.closerNotFound.sub')}</p>
      </div>

      {q.isLoading ? (
        <div className="flex flex-col gap-2" aria-hidden>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[108px] w-full rounded-[12px]" />)}
        </div>
      ) : q.isError && !q.data ? (
        <ErrorState
          icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
          title={t('adminNav.closerNotFound.errorTitle')}
          body={t('adminNav.closerNotFound.errorBody')}
          action={{ label: t('adminNav.closerNotFound.retry'), onClick: () => void q.refetch() }}
        />
      ) : q.data && q.data.rows.length === 0 && total === 0 ? (
        <EmptyState
          icon={<SearchX className="h-5 w-5" aria-hidden />}
          title={t('adminNav.closerNotFound.emptyTitle')}
          body={t('adminNav.closerNotFound.emptyBody')}
        />
      ) : (
        <>
          <ul aria-label={t('adminNav.closerNotFound.listAria')} className="m-0 p-0 list-none flex flex-col gap-2">
            {(q.data?.rows ?? []).map((row) => <NotFoundRow key={row.caseId} row={row} />)}
          </ul>
          <Pager
            page={page}
            pages={pages}
            onPage={setPage}
            busy={q.isFetching}
            prevLabel={t('adminNav.pager.prev')}
            nextLabel={t('adminNav.pager.next')}
            pageLabel={t('adminNav.pager.page', { page: page + 1, pages })}
          />
        </>
      )}
    </section>
  );
}

function NotFoundRow({ row }: { row: CloserNotFoundRow }): JSX.Element {
  const t = useT();
  const lang = useLang();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<NotFoundResolution | null>(null);
  const [failed, setFailed] = useState(false);
  const dash = '—';
  const date = (iso: string | null): string =>
    formatNy(iso, lang, { day: 'numeric', month: 'short', year: 'numeric' });
  const dateTime = (iso: string | null): string =>
    formatNy(iso, lang, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const m = useMutation({
    mutationFn: (resolution: NotFoundResolution) => closerNotFoundApi.resolve(row.caseId, resolution),
    onSuccess: () => {
      toast.success(t('adminNav.closerNotFound.done'));
      void qc.invalidateQueries({ queryKey: CLOSER_NOT_FOUND_KEYS.all });
    },
    onError: (e) => {
      const code = navError(e).code ?? null;
      if (code && GONE_CODES.has(code)) {
        toast.info(t('adminNav.closerNotFound.gone'));
        void qc.invalidateQueries({ queryKey: CLOSER_NOT_FOUND_KEYS.all });
        return;
      }
      setFailed(true);
    },
  });

  const radioName = `closer-nf-res-${row.caseId}`;
  /** Resuelto: la fila se va con el refresco; hasta entonces no se re-manda. */
  const trabado = m.isPending || m.isSuccess;

  return (
    <li
      className="rounded-[12px] border-[1.5px] border-nav-card bg-white px-4 py-3.5"
      data-testid="closer-nf-row"
      data-case-id={row.caseId}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="m-0 font-head text-[15px] font-bold text-nav-navy">{row.patient.fullName || dash}</p>
          <p className="m-0 mt-0.5 text-[13px] text-nav-slate">
            {t('adminNav.closerNotFound.medicaidId')}{' '}
            <span className="font-mono text-[14px] font-semibold text-nav-navy select-all" data-testid="closer-nf-cin">
              {row.patient.medicaidId ?? dash}
            </span>
          </p>
          <p className="m-0 mt-1.5 text-[13px] text-nav-slate">
            {t('adminNav.closerNotFound.screening', {
              date: date(row.screeningSubmittedAt),
              name: row.screenerName ?? dash,
            })}
          </p>
          <p className="m-0 text-[13px] text-nav-slate">
            {t('adminNav.closerNotFound.uploaded', {
              date: date(row.uploadedAt),
              name: row.uploaderName ?? dash,
            })}
          </p>
          <p className="m-0 text-[13px] font-semibold text-nav-amberText">
            {t('adminNav.closerNotFound.flagged', {
              date: dateTime(row.flaggedAt),
              name: row.flaggedByName ?? dash,
            })}
          </p>
          <p className="m-0 text-[13px] text-nav-slate">
            {t('adminNav.closerNotFound.owner', { name: row.ownerName ?? dash })}
          </p>
        </div>
        {!open && (
          <button
            type="button"
            className={cn(BTN_SEC, BTN_SM, 'min-h-[44px]')}
            onClick={() => { setOpen(true); setFailed(false); }}
          >
            {t('adminNav.closerNotFound.resolve')}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 border-t border-nav-hair pt-3">
          <fieldset className="m-0 p-0 border-0 min-w-0" disabled={trabado}>
            <legend className="p-0 font-head text-[13.5px] font-bold text-nav-navy mb-1.5">
              {t('adminNav.closerNotFound.howResolved')}
            </legend>
            <div className="flex flex-wrap gap-2">
              {NOT_FOUND_RESOLUTIONS.map((r) => (
                <label
                  key={r}
                  className={cn(
                    'inline-flex items-center gap-2 min-h-[44px] px-3 rounded-[10px] border-[1.5px] cursor-pointer text-[13.5px] text-nav-navy',
                    choice === r ? 'border-nav-teal bg-nav-tealTint/40' : 'border-nav-card bg-white hover:border-nav-teal',
                  )}
                >
                  <input
                    type="radio"
                    name={radioName}
                    value={r}
                    checked={choice === r}
                    onChange={() => { setChoice(r); setFailed(false); }}
                    className="accent-nav-teal"
                  />
                  {t(RESOLUTION_KEYS[r])}
                </label>
              ))}
            </div>
            <p className="m-0 mt-2 text-[12.5px] text-nav-slate">
              {t('adminNav.closerNotFound.resHint', { name: row.ownerName ?? dash })}
            </p>
            {failed && (
              <p role="alert" className="m-0 mt-2 text-[13px] font-semibold text-nav-red">
                {t('adminNav.closerNotFound.failed')}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <button
                type="button"
                className={cn(BTN_PRI, BTN_SM, 'min-h-[44px]')}
                disabled={choice === null || trabado}
                onClick={() => { if (choice) { setFailed(false); m.mutate(choice); } }}
              >
                {t('adminNav.closerNotFound.confirm')}
              </button>
              <button
                type="button"
                className={cn(BTN_SEC, BTN_SM, 'min-h-[44px]')}
                onClick={() => { setOpen(false); setChoice(null); setFailed(false); }}
              >
                {t('adminNav.closerNotFound.cancel')}
              </button>
              {choice === null && (
                <span className="text-[12.5px] text-nav-slate">{t('adminNav.closerNotFound.missing')}</span>
              )}
            </div>
          </fieldset>
        </div>
      )}
    </li>
  );
}
